/**
 * Support Effects
 * NgRx effects for support tickets side effects
 */
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, catchError, switchMap, withLatestFrom } from 'rxjs/operators';
import { SupportService } from '../services/support.service';
import * as SupportActions from './support.actions';
import * as SupportSelectors from './support.selectors';

@Injectable()
export class SupportEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private supportService = inject(SupportService);

  loadStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupportActions.loadStats),
      switchMap(() =>
        this.supportService.getStats().pipe(
          map((stats) => SupportActions.loadStatsSuccess({ stats })),
          catchError((error) => of(SupportActions.loadStatsFailure({ error: error.message })))
        )
      )
    )
  );

  loadTickets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupportActions.loadTickets),
      switchMap(({ filters, page, pageSize }) =>
        this.supportService.getTickets(filters, page, pageSize).pipe(
          map(({ tickets, total }) => SupportActions.loadTicketsSuccess({ tickets, total })),
          catchError((error) => of(SupportActions.loadTicketsFailure({ error: error.message })))
        )
      )
    )
  );

  loadTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupportActions.loadTicket),
      switchMap(({ ticketId }) =>
        this.supportService.getTicket(ticketId).pipe(
          map((ticket) => SupportActions.loadTicketSuccess({ ticket })),
          catchError((error) => of(SupportActions.loadTicketFailure({ error: error.message })))
        )
      )
    )
  );

  createTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupportActions.createTicket),
      switchMap(({ request }) =>
        this.supportService.createTicket(request).pipe(
          map((ticket) => SupportActions.createTicketSuccess({ ticket })),
          catchError((error) => of(SupportActions.createTicketFailure({ error: error.message })))
        )
      )
    )
  );

  addMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupportActions.addMessage),
      switchMap(({ ticketId, content, attachments }) =>
        this.supportService.addMessage(ticketId, content, attachments).pipe(
          map((message) => SupportActions.addMessageSuccess({ message })),
          catchError((error) => of(SupportActions.addMessageFailure({ error: error.message })))
        )
      )
    )
  );

  closeTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupportActions.closeTicket),
      switchMap(({ ticketId }) =>
        this.supportService.closeTicket(ticketId).pipe(
          map((ticket) => SupportActions.closeTicketSuccess({ ticket })),
          catchError((error) => of(SupportActions.closeTicketFailure({ error: error.message })))
        )
      )
    )
  );

  reopenTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupportActions.reopenTicket),
      switchMap(({ ticketId }) =>
        this.supportService.reopenTicket(ticketId).pipe(
          map((ticket) => SupportActions.reopenTicketSuccess({ ticket })),
          catchError((error) => of(SupportActions.reopenTicketFailure({ error: error.message })))
        )
      )
    )
  );

  reloadOnFilterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupportActions.updateFilters, SupportActions.changePage, SupportActions.changePageSize),
      withLatestFrom(
        this.store.select(SupportSelectors.selectFilters),
        this.store.select(SupportSelectors.selectCurrentPage),
        this.store.select(SupportSelectors.selectPageSize)
      ),
      map(([, filters, page, pageSize]) => SupportActions.loadTickets({ filters, page, pageSize }))
    )
  );
}

